import React, { createContext, useEffect, useReducer, useState } from "react";

import { type AlertProps } from "@icg360/design-system";

import { CLAIMS_SUPPORT, GENERAL_SUPPORT, extendedCoveragesCopy } from "consts";
import { type Coverage } from "data/keystone-api-mapping";
import { trackEvent } from "utils";

type CoveragePageState = {
  policyId: string;
  open: boolean;
  scroll_to?: number;
  type?: AlertProps["appearance"];
  title?: string;
  description?: string;
  y?: number;
};

type CoveragePageAction = {
  type: "close" | "danger" | "open" | "success" | "warning";
  policyId: string;
  y: number;
  failed?: string[];
};

const initialCoveragePageState: CoveragePageState = {
  policyId: "",
  open: false,
};

function makeCoveragesString(arr) {
  if (arr.length === 1) return arr[0];
  return (
    arr.slice(0, arr.length - 1).join(", ") + " and " + arr[arr.length - 1]
  );
}

export const CoverageContext = createContext<{
  coveragePage: CoveragePageState;
  setCoveragePage: React.Dispatch<CoveragePageAction>;
}>({
  coveragePage: initialCoveragePageState,
  setCoveragePage: () => null,
});

export function CoverageContextProvider({ children }) {
  const coverageReducer = (state, action): CoveragePageState => {
    switch (action.type) {
      case "close":
        return {
          open: false,
          scroll_to: action.y,
          policyId: "",
        };

      case "danger":
        return {
          type: "danger",
          open: true,
          policyId: action.policyId,
          title: "Something went wrong.",
          description: `We were not able to add the coverages you selected. Please try again or contact customer service at ${GENERAL_SUPPORT}.`,
        };

      case "open":
        return {
          policyId: action.policyId,
          open: true,
          y: action.y,
        };

      case "success":
        return {
          type: "success",
          open: true,
          policyId: action.policyId,
          title: "Your coverage has been updated!",
          description:
            "Your extended coverage has been added to your policy. Your updated declaration of coverage document will be available to view within the next 3-5 days.",
        };

      case "warning": {
        const { failed } = action;
        const failed_names = failed.map(
          (coverage) => extendedCoveragesCopy[coverage].short
        );
        const not_covered = `We were not able to complete your purchase of the following coverages: ${makeCoveragesString(
          failed_names
        )}.`;

        return {
          type: "warning",
          open: true,
          policyId: action.policyId,
          title: "Some coverages not added.",
          description: `${
            failed.length > 0 && not_covered
          } Please try again or contact customer service at ${CLAIMS_SUPPORT}`,
        };
      }
      default:
        return initialCoveragePageState;
    }
  };

  const [coveragePage, setCoveragePage] = useReducer(
    coverageReducer,
    initialCoveragePageState
  );

  const value = { coveragePage, setCoveragePage };

  return (
    <CoverageContext.Provider value={value}>
      {children}
    </CoverageContext.Provider>
  );
}

export const useCoverages = (coverages) => {
  const [coveragesToShow, setCoveragesToShow] = useState<Coverage[]>([]);

  useEffect(() => {
    const availableCoverages = (coverages ?? []).filter(
      (coverage) => coverage.eligibility
    );

    setCoveragesToShow(availableCoverages);

    if (availableCoverages.length) {
      trackEvent("Additional Coverage Displayed", {
        listOfCoverages: availableCoverages.map((c) => c.name),
      });
    }
  }, [coverages]);

  return coveragesToShow;
};
